import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { Box, Typography, Avatar, AvatarGroup, IconButton, Tooltip, Fade, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Reply as ReplyIcon,
  MoreHoriz as MoreHorizIcon,
  MoreVert as MoreVertIcon,
  AddReaction as AddReactionIcon,
  PushPin as PinIcon,
} from '@mui/icons-material';
import LinkPreview from '../LinkPreview';
import EmojiReactionPicker from '../EmojiReactionPicker';
import { QUICK_REACTIONS, REACTION_IMAGES, emojiToType, typeToEmoji, normalizeReactions } from '../../utils/reactions';

const MessageBubble = ({ 
  message, 
  isFirstInGroup, 
  isLastInGroup, 
  currentUserId,
  conversationParticipants = [],
  quickReactionEmoji = '👍',
  showStatus = false,
  isPinned = false,
  themeColor = '#007AFF',
  onPin,
  onUnpin,
  onVisible,
  onReply,
  onReact,
  onRemoveReaction,
  onUnsend,
  onForward,
  onReport,
  onCopy,
}) => {
  const isCurrentUser = message.sender?.id === currentUserId;
  const isOnlyInGroup = isFirstInGroup && isLastInGroup;
  const isMiddleInGroup = !isFirstInGroup && !isLastInGroup;
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const bubbleRef = useRef(null);
  const hideActionsTimeoutRef = useRef(null);
  
  // Quick reaction states
  const [isHolding, setIsHolding] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [showQuickReactions, setShowQuickReactions] = useState(false);
  const holdTimerRef = useRef(null);
  const holdStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const HOLD_DURATION = 500; // 500ms to trigger

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
      const isoWithoutTz = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value);
      return new Date(isoWithoutTz ? `${value}Z` : value);
    }
    return new Date(value);
  };

  const formatTime = (date) => {
    const parsed = parseDate(date);
    if (!parsed || Number.isNaN(parsed.getTime())) return '';
    return format(parsed, 'h:mm a');
  };

  // Helper to check if message should have no bubble padding (media/emoji only)
  const isMediaOrEmoji = () => {
    if (message.type === 'image' || message.type === 'gif' || message.type === 'emoji') return true;
    // Check if message is emoji-only (no padding for pure emoji messages)
    if (message.type === 'text' || !message.type) {
      const raw = message?.content;
      const contentStr = typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
      const trimmed = contentStr.trim();
      
      // Emoji regex that excludes digits (0-9) and common punctuation
      // Digits are technically in \p{Emoji} in Unicode, so we need to exclude them
      const emojiOnlyRegex = /^(\p{Emoji}(?<![0-9])|\s)+$/u;
      const isEmojiOnly = emojiOnlyRegex.test(trimmed);
      
      // Additional check: if content contains only digits/spaces, it's NOT emoji-only
      const digitOnlyRegex = /^[0-9\s]+$/;
      const isDigitOnly = digitOnlyRegex.test(trimmed);
      
      const result = isEmojiOnly && !isDigitOnly;
      return result;
    }
    return false;
  };

  const renderMessageContent = () => {
    switch (message.type) {
      case 'system':
        // Personalize system message: show "You" for current user, use nickname for others
        let displayContent = message.content;
        const senderName = message.sender?.profile?.displayName || message.sender?.username;
        
        if (message.sender?.id === currentUserId) {
          // Replace sender's name with "You" for current user
          if (senderName && displayContent.startsWith(senderName)) {
            displayContent = displayContent.replace(senderName, 'You');
          }
        } else if (message.sender?.id) {
          // For other users, check if there's a nickname set
          const participant = conversationParticipants.find(
            p => (p.user?.id || p.participantId) === message.sender.id
          );
          const nickname = participant?.nickname;
          
          if (nickname && senderName && displayContent.startsWith(senderName)) {
            // Replace the display name with the nickname
            displayContent = displayContent.replace(senderName, nickname);
          }
        }
        
        return (
          <Box
            sx={{
              textAlign: 'center',
              py: 1,
              px: 2,
              backgroundColor: 'action.hover',
              borderRadius: 2,
              fontSize: '0.875rem',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {displayContent}
            </Typography>
          </Box>
        );
      
      case 'image': {
        const imageUrl = message.content || message.metadata?.url;
        const imageCaption = message.metadata?.caption;
        
        if (!imageUrl) {
          return (
            <Box
              sx={{
                width: 200,
                height: 150,
                borderRadius: 2,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {message.pending ? 'Uploading...' : 'Image unavailable'}
              </Typography>
            </Box>
          );
        }
        
        return (
          <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
            <Box
              component="img"
              src={imageUrl}
              alt="Shared image"
              onError={(e) => {
                console.error('[MessageBubble] Image failed to load:', imageUrl);
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"%3E%3Crect fill="%23f0f0f0" width="200" height="150"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage failed to load%3C/text%3E%3C/svg%3E';
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 2,
                cursor: 'pointer',
                display: 'block',
              }}
            />
            {imageCaption && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
                  borderRadius: '0 0 8px 8px',
                  px: 1,
                  pt: 2,
                  pb: 0.75,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    fontSize: '0.85rem',
                  }}
                >
                  {imageCaption}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }
      
      case 'gif': {
        const gifCaption = message.metadata?.caption;
        return (
          <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
            <Box
              component="img"
              src={message.metadata?.url || message.content}
              alt="GIF"
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 2,
                display: 'block',
              }}
            />
            {gifCaption && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
                  borderRadius: '0 0 8px 8px',
                  px: 1,
                  pt: 2,
                  pb: 0.75,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    fontSize: '0.85rem',
                  }}
                >
                  {gifCaption}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }
      
      case 'emoji':
        // Get saved emoji size from metadata (1x to 4x scale)
        const savedEmojiSize = message.metadata?.emojiSize || 1;
        // Calculate scale based on hold progress for emoji messages
        const emojiScale = isHolding ? 1 + (holdProgress / 100) * 1.5 : savedEmojiSize;
        const shouldPop = holdProgress >= 100;
        
        // Base font size scales with the saved size (48px base * size multiplier)
        const baseFontSize = 48 * savedEmojiSize;
        
        return (
          <Typography
            sx={{
              fontSize: `${baseFontSize}px`,
              lineHeight: 1,
              transform: shouldPop ? 'scale(0)' : (isHolding ? `scale(${emojiScale / savedEmojiSize})` : 'scale(1)'),
              transition: shouldPop ? 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'transform 0.2s ease-out',
              opacity: shouldPop ? 0 : 1,
            }}
          >
            {message.content}
          </Typography>
        );
      
      case 'voice':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>🎤 Voice message</Typography>
            <Typography variant="caption">
              {message.metadata?.duration || '0:00'}
            </Typography>
          </Box>
        );
      
      case 'deleted':
        return (
          <Typography
            variant="body2"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
            }}
          >
            This message was deleted
          </Typography>
        );
      
      default:
        return (
          <Box>
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {message.content}
            </Typography>
            {message.linkPreview && (
              <Box sx={{ mt: 1 }}>
                <LinkPreview linkData={message.linkPreview} variant="compact" />
              </Box>
            )}
          </Box>
        );
    }
  };

  const renderStatus = () => {
    if (!isCurrentUser || !showStatus) return null;

    if (message.pending || message.status === 'sending') {
      return <CircularProgress size={14} sx={{ color: 'text.secondary' }} />;
    }

    if (message.status === 'failed') {
      return <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>!</Typography>;
    }

    const recipients = Array.isArray(message.recipients) ? message.recipients : [];
    const hasSeen = recipients.some((r) => r?.status === 'seen');
    const hasDelivered = recipients.some((r) => r?.status === 'delivered' || r?.status === 'seen');
    const effectiveStatus = message.status || (hasSeen ? 'seen' : (hasDelivered ? 'delivered' : 'sent'));

    const seenMap = new Map();
    recipients
      .filter((r) => r?.status === 'seen')
      .map((r) => r?.user)
      .filter(Boolean)
      .forEach((u) => {
        if (!u?.id) return;
        if (u.id === currentUserId) return;
        seenMap.set(u.id, u);
      });

    const senderUser = message?.sender;
    if (senderUser?.id && senderUser.id !== currentUserId) {
      seenMap.set(senderUser.id, senderUser);
    }

    const seenUsers = Array.from(seenMap.values());

    switch (effectiveStatus) {
      case 'sent':
        return <DoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
      case 'delivered':
        return <DoneAllIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
      case 'seen':
        if (seenUsers.length > 0) {
          return (
            <AvatarGroup
              max={3}
              sx={{
                '& .MuiAvatar-root': {
                  width: 16,
                  height: 16,
                  fontSize: 10,
                  border: '2px solid',
                  borderColor: 'background.paper',
                },
              }}
            >
              {seenUsers.map((u) => (
                <Avatar
                  key={u.id}
                  src={u?.profile?.avatar || undefined}
                  alt={u?.profile?.displayName || u?.username || 'User'}
                >
                  {(u?.profile?.displayName || u?.username || 'U')[0]?.toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
          );
        }
        return <DoneAllIcon sx={{ fontSize: 16, color: '#4fc3f7' }} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!onVisible || isCurrentUser) return;
    const element = bubbleRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          onVisible(message);
        }
      },
      { threshold: 0.6 }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [onVisible, isCurrentUser, message]);

  const getSenderName = () => {
    if (isCurrentUser) return 'You';
    
    // Check if there's a nickname set for this sender
    if (message.sender?.id && conversationParticipants.length > 0) {
      const senderId = message.sender.id;
      const participant = conversationParticipants.find(p => {
        const participantUserId = p.user?.id || p.participantId || p.id;
        return participantUserId === senderId;
      });
      
      if (participant?.nickname) {
        return participant.nickname;
      }
    }
    
    return message.sender?.profile?.displayName || 
           message.sender?.username || 
           'User';
  };

  const handleReactionClick = (event) => {
    setReactionAnchor(event.currentTarget);
  };

  const handleReactionClose = () => {
    setReactionAnchor(null);
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleReactionSelect = (reaction) => {
    const type = reaction?.type || emojiToType(reaction?.emoji) || reaction?.emoji;
    const emoji = typeToEmoji(type) || reaction?.emoji || type;
    
    // Check if user already has this reaction - toggle off if so
    const userHasThisReaction = message.reactions?.find((r) => {
      const uid = r.userId || r.user?.id;
      const val = r.emoji || r.type;
      const valEmoji = typeToEmoji(emojiToType(val) || val) || val;
      return uid === currentUserId && valEmoji === emoji;
    });
    
    try {
      console.log('[MessageBubble] reaction select', { 
        messageId: message.id, 
        type, 
        emoji, 
        userHasThisReaction: !!userHasThisReaction 
      });
    } catch (e) {}
    
    if (userHasThisReaction && onRemoveReaction) {
      onRemoveReaction(message.id, emoji);
    } else if (emoji && onReact) {
      onReact(message.id, emoji);
    }
  };

  const handleReactionPress = (reaction) => {
    // Check if current user already reacted with this emoji
    const pressedType = reaction?.type || emojiToType(reaction?.emoji) || reaction?.emoji;
    const pressedEmoji = typeToEmoji(pressedType) || reaction?.emoji || pressedType;
    const userReaction = message.reactions?.find((r) => {
      const uid = r.userId || r.user?.id;
      const val = r.emoji || r.type;
      const valEmoji = typeToEmoji(emojiToType(val) || val) || val;
      return uid === currentUserId && valEmoji === pressedEmoji;
    });

    try {
      console.log('[MessageBubble] reaction press', {
        messageId: message.id,
        pressedType,
        pressedEmoji,
        hasUserReaction: !!userReaction,
      });
    } catch (e) {}
    
    if (userReaction && onRemoveReaction) {
      onRemoveReaction(message.id, pressedEmoji);
    } else if (onReact) {
      onReact(message.id, pressedEmoji);
    }
  };

  // Aggregate reactions by emoji
  const aggregateReactions = () => {
    const normalized = normalizeReactions(message.reactions);
    if (!normalized || normalized.length === 0) return [];

    // Hard de-dupe: keep only 1 reaction per user (latest by `at` if present)
    const byUser = new Map();
    normalized.forEach((r) => {
      const existing = byUser.get(r.userId);
      if (!existing) {
        byUser.set(r.userId, r);
        return;
      }

      const existingAt = existing?.at ? new Date(existing.at).getTime() : 0;
      const nextAt = r?.at ? new Date(r.at).getTime() : 0;
      if (nextAt >= existingAt) {
        byUser.set(r.userId, r);
      }
    });

    const deduped = Array.from(byUser.values());

    const reactionMap = new Map();
    deduped.forEach((reaction) => {
      const emoji = reaction.emoji;
      if (!emoji) return;

      if (!reactionMap.has(emoji)) {
        reactionMap.set(emoji, {
          emoji,
          count: 0,
          users: [],
          hasCurrentUser: false,
        });
      }

      const data = reactionMap.get(emoji);
      data.count++;
      data.users.push(reaction.userId);
      if (reaction.userId === currentUserId) {
        data.hasCurrentUser = true;
      }
    });
    
    return Array.from(reactionMap.values()).sort((a, b) => b.count - a.count);
  };

  const reactions = aggregateReactions();

  const normalizedReactions = normalizeReactions(message.reactions);
  const mySelectedReactions = normalizedReactions.filter((r) => r.userId === currentUserId);
  const uniqueReactorsCount = (() => {
    const ids = new Set(normalizedReactions.map((r) => r.userId).filter(Boolean));
    return ids.size;
  })();

  const topReactions = reactions.slice(0, 3);
  const remainingReactionsCount = Math.max(0, reactions.length - topReactions.length);

  const quickReactionType = emojiToType(quickReactionEmoji) || quickReactionEmoji;
  const quickReactionImg = REACTION_IMAGES[quickReactionType];

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (hideActionsTimeoutRef.current) clearTimeout(hideActionsTimeoutRef.current);
    };
  }, []);

  // Handle quick tap for default like reaction
  const handleQuickTap = () => {
    if (!isHolding && onReact) {
      // Check if user already reacted with the quick reaction emoji
      const hasReacted = message.reactions?.find((r) => {
        const uid = r.userId || r.user?.id;
        const val = r.emoji || r.type;
        const valEmoji = typeToEmoji(emojiToType(val) || val) || val;
        return uid === currentUserId && valEmoji === (typeToEmoji(quickReactionType) || quickReactionType);
      });

      const quickEmoji = typeToEmoji(quickReactionType) || quickReactionType;
      
      if (hasReacted && onRemoveReaction) {
        onRemoveReaction(message.id, quickEmoji);
      } else {
        onReact(message.id, quickEmoji);
      }
    }
  };

  // Handle hold start
  const handleHoldStart = (e) => {
    e.preventDefault();
    setIsHolding(true);
    holdStartTimeRef.current = Date.now();
    
    // Animate progress
    const animate = () => {
      const elapsed = Date.now() - holdStartTimeRef.current;
      const progress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setHoldProgress(progress);
      
      if (progress < 100) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Hold complete - show emoji picker
        setShowQuickReactions(true);
        setIsHolding(false);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  // Handle hold end
  const handleHoldEnd = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // If hold was less than threshold, treat as quick tap
    if (isHolding && holdProgress < 100) {
      handleQuickTap();
    }
    
    setIsHolding(false);
    setHoldProgress(0);
    holdStartTimeRef.current = null;
  };

  // Handle quick reaction select
  const handleQuickReactionSelect = (type) => {
    if (onReact) {
      const emoji = typeToEmoji(type) || type;
      try {
        console.log('[MessageBubble] quick reaction select', { messageId: message.id, type, emoji });
      } catch (e) {}
      onReact(message.id, emoji);
    }
    setShowQuickReactions(false);
  };

  const handleMouseEnter = () => {
    if (hideActionsTimeoutRef.current) {
      clearTimeout(hideActionsTimeoutRef.current);
      hideActionsTimeoutRef.current = null;
    }
    setShowActions(true);
  };

  const handleMouseLeave = () => {
    // Add a delay before hiding actions to give user time to move cursor to them
    hideActionsTimeoutRef.current = setTimeout(() => {
      setShowActions(false);
    }, 300);
  };

  // System messages (pin/unpin notifications) - centered layout
  if (message.type === 'system') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          my: 1,
        }}
      >
        {renderMessageContent()}
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        flexDirection: isCurrentUser ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        mb: isLastInGroup ? 2 : 0.5,
        gap: 1,
        width: '100%',
        px: 0,
        position: 'relative',
      }}
    >
      {/* Avatar - only show on last message in group for other users */}
      {!isCurrentUser && isLastInGroup ? (
        <Avatar
          src={message.sender?.profile?.avatar}
          alt={getSenderName()}
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        >
          {getSenderName()[0]?.toUpperCase()}
        </Avatar>
      ) : !isCurrentUser ? (
        <Box sx={{ width: 32, flexShrink: 0 }} />
      ) : null}

      {/* Message Bubble */}
      <Box
        ref={bubbleRef}
        sx={{
          maxWidth: { xs: '85%', sm: '70%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
          position: 'relative',
        }}
      >
        {/* Quick Actions - Facebook Messenger style */}
        {showActions && (
          <Box
            sx={{
              position: 'absolute',
              top: -34,
              [isCurrentUser ? 'right' : 'left']: 0,
              display: 'flex',
              flexDirection: isCurrentUser ? 'row-reverse' : 'row',
              justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
              alignItems: 'center',
              gap: 0.25,
              zIndex: 10,
              width: 'max-content',
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            {/* 3-dot More Menu */}
            <Tooltip title="More">
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{ 
                  p: 0.5,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
                }}
              >
                <MoreHorizIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* Reply */}
            <Tooltip title="Reply">
              <IconButton
                size="small"
                onClick={() => onReply && onReply(message)}
                sx={{ 
                  p: 0.5,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
                }}
              >
                <ReplyIcon sx={{ fontSize: 18, transform: isCurrentUser ? 'scaleX(-1)' : 'none' }} />
              </IconButton>
            </Tooltip>
            {/* React */}
            <Tooltip title="React">
              <IconButton
                size="small"
                onClick={handleReactionClick}
                sx={{ 
                  p: 0.5,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
                }}
              >
                <AddReactionIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (isPinned) {
                if (onUnpin) onUnpin(message);
              } else {
                if (onPin) onPin(message);
              }
            }}
            disabled={!onPin && !onUnpin}
          >
            <ListItemIcon>
              <PinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={isPinned ? 'Unpin message' : 'Pin message'} />
          </MenuItem>
        </Menu>
        {/* Sender name - only show on first message in group for other users */}
        {!isCurrentUser && isFirstInGroup && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, mb: 0.5 }}
          >
            {getSenderName()}
          </Typography>
        )}

        {/* Reply preview if exists */}
        {message.replyTo && (
          <Box
            sx={{
              backgroundColor: isCurrentUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
              borderLeft: `3px solid ${isCurrentUser ? '#fff' : themeColor}`,
              borderRadius: 1,
              p: 1,
              mb: 0.5,
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {(message.replyTo.type === 'image' || message.replyTo.type === 'gif') && (
              <Box
                component="img"
                src={message.replyTo.content || message.replyTo.metadata?.url}
                alt="reply"
                sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight="600" display="block">
                Replying to {message.replyTo.sender?.profile?.displayName || message.replyTo.sender?.username || 'User'}
              </Typography>
              <Typography variant="caption" noWrap display="block">
                {message.replyTo.type === 'image'
                  ? (message.replyTo.metadata?.caption || '📷 Photo')
                  : message.replyTo.type === 'gif'
                  ? (message.replyTo.metadata?.caption || 'GIF')
                  : message.replyTo.content}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Timestamp (shown on click) */}
        {showTimestamp && (
          <Box
            sx={{
              textAlign: isCurrentUser ? 'right' : 'left',
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
              }}
            >
              {formatTime(message.createdAt)}
            </Typography>
          </Box>
        )}

        {/* Message Content */}
        <Box
          onMouseDown={handleHoldStart}
          onMouseUp={handleHoldEnd}
          onMouseLeave={handleHoldEnd}
          // onTouchStart={handleHoldStart}
          // onTouchEnd={handleHoldEnd}
          onClick={() => setShowTimestamp(!showTimestamp)}
          sx={{
            cursor: 'pointer',
            backgroundColor: isMediaOrEmoji() ? 'transparent' : (isCurrentUser ? themeColor : '#E5E5EA'),
            color: isCurrentUser ? '#fff' : '#000',
            px: isMediaOrEmoji() ? 0 : 2,
            py: isMediaOrEmoji() ? 0 : 1.25,
            boxShadow: isMediaOrEmoji() ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
            position: 'relative',
            transform: isHolding ? `scale(${1 + (holdProgress / 100) * 0.05})` : 'scale(1)',
            transition: isHolding ? 'none' : 'transform 0.2s ease-out',
            // Dynamic border radius based on position in group - matching React Native
            ...(isOnlyInGroup && {
              borderRadius: '18px',
            }),
            ...(isFirstInGroup && !isOnlyInGroup && isCurrentUser && {
              borderTopRightRadius: '18px',
              borderBottomRightRadius: '4px',
              borderTopLeftRadius: '18px',
              borderBottomLeftRadius: '18px',
            }),
            ...(isFirstInGroup && !isOnlyInGroup && !isCurrentUser && {
              borderTopLeftRadius: '18px',
              borderBottomLeftRadius: '4px',
              borderTopRightRadius: '18px',
              borderBottomRightRadius: '18px',
            }),
            ...(isMiddleInGroup && isCurrentUser && {
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              borderTopLeftRadius: '18px',
              borderBottomLeftRadius: '18px',
            }),
            ...(isMiddleInGroup && !isCurrentUser && {
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '4px',
              borderTopRightRadius: '18px',
              borderBottomRightRadius: '18px',
            }),
            ...(isLastInGroup && !isOnlyInGroup && isCurrentUser && {
              borderBottomRightRadius: '18px',
              borderTopRightRadius: '4px',
              borderTopLeftRadius: '18px',
              borderBottomLeftRadius: '18px',
            }),
            ...(isLastInGroup && !isOnlyInGroup && !isCurrentUser && {
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '18px',
              borderTopRightRadius: '18px',
              borderBottomRightRadius: '18px',
            }),
          }}
        >
          {isPinned && !isMediaOrEmoji() && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: isCurrentUser ? -8 : 'auto',
                left: isCurrentUser ? 'auto' : -8,
                width: 18,
                height: 18,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}
            >
              <PinIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            </Box>
          )}
          {renderMessageContent()}
          
          {/* Hold Progress Indicator */}
          {isHolding && holdProgress < 100 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                left: '50%',
                transform: 'translateX(-50%)',
                width: `${holdProgress}%`,
                height: 3,
                backgroundColor: themeColor,
                borderRadius: 2,
                transition: 'width 0.05s linear',
              }}
            />
          )}
        </Box>

        {isCurrentUser && showStatus && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              mt: 0.5,
              minHeight: 16,
            }}
          >
            {renderStatus()}
          </Box>
        )}
        
        {/* Quick Reaction Picker - Appears on Hold */}
        {showQuickReactions && (
          <Fade in={showQuickReactions}>
            <Box
              sx={{
                position: 'absolute',
                [isCurrentUser ? 'right' : 'left']: 0,
                bottom: '100%',
                mb: 1,
                display: 'flex',
                gap: 1,
                p: 1.5,
                backgroundColor: 'background.paper',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                zIndex: 1000,
                animation: 'popIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                '@keyframes popIn': {
                  '0%': {
                    transform: 'scale(0.3)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }}
              onMouseLeave={() => setShowQuickReactions(false)}
            >
              {QUICK_REACTIONS.map((reaction, index) => (
                <IconButton
                  key={reaction.type}
                  onClick={() => handleQuickReactionSelect(reaction.type)}
                  sx={{
                    p: 0.5,
                    minWidth: 'auto',
                    animation: `emojiPop 0.3s ease-out ${index * 0.05}s both`,
                    '@keyframes emojiPop': {
                      '0%': {
                        transform: 'scale(0) translateY(20px)',
                        opacity: 0,
                      },
                      '50%': {
                        transform: 'scale(1.2) translateY(-5px)',
                      },
                      '100%': {
                        transform: 'scale(1) translateY(0)',
                        opacity: 1,
                      },
                    },
                    '&:hover': {
                      transform: 'scale(1.3)',
                      backgroundColor: 'rgba(0,0,0,0.05)',
                    },
                    transition: 'transform 0.2s',
                  }}
                >
                  <Box
                    component="img"
                    src={reaction.image}
                    alt={reaction.label}
                    sx={{ width: 24, height: 24, display: 'block', pointerEvents: 'none' }}
                  />
                </IconButton>
              ))}
            </Box>
          </Fade>
        )}

        {/* Reactions */}
        {reactions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              mt: 0.5,
              flexWrap: 'wrap',
            }}
          >
            {topReactions.map((reaction, index) => (
              <Tooltip
                key={index}
                title={`${reaction.count} ${reaction.count === 1 ? 'reaction' : 'reactions'} • ${uniqueReactorsCount} ${uniqueReactorsCount === 1 ? 'person' : 'people'}`}
              >
                <Box
                  onClick={() => handleReactionPress(reaction)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    backgroundColor: reaction.hasCurrentUser ? 'rgba(0, 122, 255, 0.1)' : 'background.paper',
                    border: reaction.hasCurrentUser ? '1.5px solid' : '1px solid',
                    borderColor: reaction.hasCurrentUser ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {(() => {
                    const type = emojiToType(reaction.emoji) || reaction.emoji;
                    const imgSrc = REACTION_IMAGES[type];
                    const fallbackEmoji = typeToEmoji(type);
                    return imgSrc ? (
                      <Box
                        component="img"
                        src={imgSrc}
                        alt={type}
                        sx={{ width: 16, height: 16, display: 'block', pointerEvents: 'none' }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '14px', lineHeight: 1 }}>{fallbackEmoji}</Typography>
                    );
                  })()}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '11px',
                      fontWeight: reaction.hasCurrentUser ? 600 : 400,
                      color: reaction.hasCurrentUser ? themeColor : 'text.secondary',
                    }}
                  >
                    {reaction.count}
                  </Typography>
                </Box>
              </Tooltip>
            ))}

            {remainingReactionsCount > 0 && (
              <Tooltip title={`+${remainingReactionsCount} more`}>
                <Box
                  onClick={handleReactionClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: 'text.secondary',
                    }}
                  >
                    +{remainingReactionsCount}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      <EmojiReactionPicker
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        onClose={handleReactionClose}
        onReactionSelect={handleReactionSelect}
        selectedReactions={mySelectedReactions}
      />
    </Box>
  );
};

export default MessageBubble;
